import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import { throwApiError } from "@src/packages/api/api-error";

type FETHistoryPath =
  paths["/patients/{patientId}/frozen-embryo-transfer-history"];

export type FETHistoricalRecord = NonNullable<
  ResponseBody<FETHistoryPath["get"]>["data"]
>[number];

export type FETHistoricalRecordInput = RequestBody<FETHistoryPath["post"]>;

const FET_HISTORY_QUERY_KEY = "fet-historical-records";

export function useFETHistoricalRecords() {
  const { client, tenantId, patientId } = useApiClient();

  return useQuery<FETHistoricalRecord[]>({
    queryKey: [FET_HISTORY_QUERY_KEY, tenantId, patientId],
    queryFn: async () => {
      const result = await client.GET(
        "/patients/{patientId}/frozen-embryo-transfer-history",
        {
          params: { path: { patientId } },
        },
      );

      if (result.data) return result.data.data ?? [];
      throwApiError(
        result.error,
        "Failed to load frozen embryo transfer history",
        FET_HISTORY_QUERY_KEY,
      );
    },
    enabled: Boolean(patientId && tenantId),
  });
}

export function useSaveFETHistoricalRecord() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: FETHistoricalRecordInput) => {
      const result = await client.POST(
        "/patients/{patientId}/frozen-embryo-transfer-history",
        {
          params: { path: { patientId } },
          body: input,
        },
      );

      if (result.data) return result.data.data;
      throwApiError(
        result.error,
        "Failed to save frozen embryo transfer history",
        FET_HISTORY_QUERY_KEY,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [FET_HISTORY_QUERY_KEY, tenantId, patientId],
      });
    },
  });
}
